<template>
  <div class="modal-overlay top z1100 fc-overlay" @click.self="$emit('close')">
    <div class="fc-modal" role="dialog" aria-modal="true" aria-label="Unit card">
      <div class="fc-actions">
        <slot name="actions" />
        <button v-if="shareUrl" class="fc-build" @click="copy(shareUrl, 'link')" title="Copy a link that opens this exact unit's card">{{ copied === 'link' ? '✓ Link copied' : '🔗 Link' }}</button>
        <button class="fc-build" @click="copy(code, 'code')" title="Copy this unit as the stream's memory-card text block (the Builder's Character export)">{{ copied === 'code' ? '✓ Code copied' : '📋 Copy code' }}</button>
        <button class="fc-build" @click="openInBuilder" aria-label="Open this unit in the Character Builder (new tab)" title="Open a copy of this unit in the Character Builder (new tab)">✏️ Builder</button>
      </div>
      <button class="fc-close" @click="$emit('close')" aria-label="Close">✕</button>
      <InlineUnit :unit="unit" :inModal="true" :teamColor="teamColor || 'Champion'"
                  :classRank="classRank" :toolTips="toolTips"
                  :notAbles="notAbles" :charStats="charStats" :itemschanged="itemschanged"
                  :abilitiesInfo="abilitiesInfo" :highlightColors="highlightColors"
                  :enemy-team="enemyTeam" :own-team="ownTeam"
                  :show-win-rates="showWinRates" />
    </div>
  </div>
</template>

<script>
import InlineUnit from './inlineUnit.vue';
import modalA11y from '../mixins/modalA11y.js';
import { encodeBuild, exportUnit } from '../lib/streamFormat.js';

export default {
  mixins: [modalA11y],
  components: { InlineUnit },
  props: {
    unit: { type: Object, required: true },
    teamColor: { type: String, default: 'Champion' },
    classRank: { type: Object, default: () => ({}) },
    toolTips: { type: Object, default: () => ({}) },
    notAbles: { type: Object, required: true },
    charStats: { type: Object, required: true },
    itemschanged: { type: Object, required: true },
    abilitiesInfo: { type: Object, default: () => ({}) },
    highlightColors: { type: Object, default: () => new Map() },
    // Opposing / own rosters (bracket only) — threaded through so the modal's
    // card shows the same both-teams ability breakdown as the grid card.
    enemyTeam: { type: Array, default: () => [] },
    ownTeam: { type: Array, default: () => [] },
    showWinRates: { type: Boolean, default: false },
    // A URL that reopens this card (the profile's Units tab passes one); shows a 🔗 button.
    shareUrl: { type: String, default: '' },
  },
  emits: ['close'],
  data() { return { copied: '' }; },
  computed: {
    code() { return exportUnit(this.unit, ''); },
  },
  methods: {
    copy(text, what) {
      const done = () => { this.copied = what; window.setTimeout(() => { if (this.copied === what) this.copied = ''; }, 1500); };
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch (e) { /* ignore */ }
        document.body.removeChild(ta); done();
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, fallback);
      else fallback();
    },
    // Copy this exact unit into the Character Builder (new tab, so the bracket stays open).
    openInBuilder() {
      if (!this.unit || !this.unit.Name) return;  // BuilderView ignores a nameless payload — don't open a blank tab
      const url = window.location.origin + window.location.pathname + '?build&u=' + encodeBuild(this.unit, this.unit.Name);
      window.open(url, '_blank', 'noopener');
    },
  },
};
</script>

<style scoped>
.fc-overlay {
  background: rgba(0, 0, 0, 0.65);
  padding: 24px 12px;
}
.fc-modal {
  position: relative;
  background: #0e1622;
  border: 1px solid #2f5d86;
  border-radius: 8px;
  padding: 16px 18px;
  width: min(1200px, 96vw);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.55);
}
.fc-close {
  position: absolute; top: 8px; right: 10px;
  background: none; border: none; color: #cbe6ff; font-size: 18px; cursor: pointer; z-index: 1;
}
.fc-actions {
  position: absolute; top: 8px; right: 38px; z-index: 1;
  display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px; max-width: calc(100% - 60px);
}
.fc-build {
  background: #163049; border: 1px solid #2f5d86; border-radius: 5px;
  color: #cbe6ff; font-size: 12px; padding: 2px 8px; cursor: pointer;
}
.fc-build:hover { background: #1d456b; }

/* Designed landscape layout: a left Profile column (identity, stats, defense,
   gear) and a right Abilities column (innates on top, action-skill lists in two
   sub-columns, elemental + tuning as a footer). */
.fc-modal :deep(.unit) { width: 100%; }
.fc-modal :deep(.bg-and-holder) {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  text-align: left;
}

/* Profile column (left) */
.fc-modal :deep(.profile-col) {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 0 0 34%;
  max-width: 34%;
}
.fc-modal :deep(.profile-col .image-stats) {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.fc-modal :deep(.profile-col .name-and-stats) { flex: 1; min-width: 0; }

/* Abilities column (right): innates full width, skill lists in 2 sub-columns,
   elemental + tuning paired as a footer. */
.fc-modal :deep(.abilities-col) {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 8px 12px;
}
.fc-modal :deep(.abilities-col > *) { margin: 0; }
.fc-modal :deep(.abilities-col .unit-innates) { flex: 1 1 100%; order: 1; }
.fc-modal :deep(.abilities-col .unit-skills) { flex: 1 1 45%; min-width: 220px; order: 2; }
.fc-modal :deep(.abilities-col .element-affinity) { flex: 1 1 45%; min-width: 220px; order: 3; }
.fc-modal :deep(.abilities-col .damage-tuning) { flex: 1 1 45%; min-width: 220px; order: 3; }
</style>
